/**
 * Theme utilities for system theme detection and management
 */

export type Theme = 'light' | 'dark'

/**
 * Get the current system theme preference
 */
export function getSystemTheme(): Theme {
    if (typeof window === 'undefined') return 'light'

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Check if the system is currently in dark mode
 */
export function isDarkMode(): boolean {
    return getSystemTheme() === 'dark'
}

/**
 * Listen for system theme changes
 */
export function watchSystemTheme(callback: (theme: Theme) => void): () => void {
    if (typeof window === 'undefined') return () => { }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
        callback(e.matches ? 'dark' : 'light')
    }

    // Add listener
    mediaQuery.addEventListener('change', handleChange)

    // Return cleanup function
    return () => {
        mediaQuery.removeEventListener('change', handleChange)
    }
}

/**
 * Get theme-aware colors for charts
 */
export function getThemeColors() {
    const isDark = isDarkMode()

    return {
        background: isDark ? '#1a1a1a' : '#ffffff',
        text: isDark ? '#ffffff' : '#000000',
        grid: isDark ? '#333333' : '#e5e7eb',
        border: isDark ? '#374151' : '#d1d5db',
        bullish: '#22c55e', // Green for bullish candles
        bearish: isDark ? '#ef4444' : '#dc2626', // Red for bearish candles
        indicators: {
            sma: '#3b82f6',
            ema: '#8b5cf6',
            rsi: '#f59e0b',
            macd: '#10b981'
        },
        projections: {
            support: '#22c55e',
            resistance: '#ef4444',
            target: '#f59e0b'
        }
    }
}

/**
 * Convert CSS custom property to hex color
 */
export function cssVarToHex(cssVar: string): string {
    if (!cssVar) return '#000000'

    // If it's already a hex color, return it
    if (cssVar.startsWith('#')) return cssVar

    // If it's an hsl value or hsl-like value, convert it
    if (cssVar.includes('%') || cssVar.startsWith('hsl')) {
        let hslValue = cssVar

        // If it's missing the hsl() wrapper, add it
        if (!cssVar.startsWith('hsl')) {
            hslValue = `hsl(${cssVar})`
        }

        // Create a temporary element to compute the color
        const tempEl = document.createElement('div')
        tempEl.style.color = hslValue
        document.body.appendChild(tempEl)
        const computedColor = getComputedStyle(tempEl).color
        document.body.removeChild(tempEl)
        return computedColor
    }

    // If it's an rgb value, return as is
    if (cssVar.startsWith('rgb')) {
        return cssVar
    }

    return cssVar
}

/**
 * Get computed theme-aware colors from CSS custom properties
 */
export function getComputedThemeColors() {
    const computedStyle = getComputedStyle(document.documentElement)

    return {
        foreground: cssVarToHex(computedStyle.getPropertyValue('--foreground').trim()),
        border: cssVarToHex(computedStyle.getPropertyValue('--border').trim()),
        destructive: cssVarToHex(computedStyle.getPropertyValue('--destructive').trim()),
        primary: cssVarToHex(computedStyle.getPropertyValue('--primary').trim()),
        background: cssVarToHex(computedStyle.getPropertyValue('--background').trim()),
    }
}
